import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Box,
  Typography,
  styled,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as MuiIcons from '@mui/icons-material';

import MUIIconNameSynonyms from '../utils/MUIIconNameSynonyms';

// Simple in-memory search implementation
const createSearchIndex = () => {
  const allIcons = Object.keys(MuiIcons).sort();
  const searchData: Array<{ name: string; searchTerms: string }> = [];

  allIcons.forEach((iconName) => {
    let searchTerms = iconName.toLowerCase();

    // Add synonyms if available
    try {
      if (
        MUIIconNameSynonyms &&
        MUIIconNameSynonyms[iconName as keyof typeof MUIIconNameSynonyms]
      ) {
        searchTerms += ` ${MUIIconNameSynonyms[
          iconName as keyof typeof MUIIconNameSynonyms
        ].toLowerCase()}`;
      }
    } catch (error) {
      // Silently continue if synonyms can't be loaded
      console.warn(`Could not load synonyms for icon: ${iconName}`, error);
    }

    searchData.push({
      name: iconName,
      searchTerms,
    });
  });

  return {
    search: (query: string, limit = 100) => {
      if (!query.trim()) {
        return allIcons.slice(0, limit);
      }

      const lowerQuery = query.toLowerCase();
      const results = searchData
        .filter(({ searchTerms }) => searchTerms.includes(lowerQuery))
        .map(({ name }) => name)
        .slice(0, limit);

      return results;
    },
  };
};

const searchIndex = createSearchIndex();

const StyledIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  transition: theme.transitions.create(['background-color', 'transform'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-2px)',
  },
  minWidth: 80,
  minHeight: 80,
}));

const IconName = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  textAlign: 'center',
  wordBreak: 'break-word',
  color: theme.palette.text.secondary,
  lineHeight: 1.2,
}));

export interface IconSearchComponentProps {
  open: boolean;
  onClose: () => void;
  onSelectIcon: (iconName: string) => void;
  currentIcon?: string;
}

const IconSearchComponent: React.FC<IconSearchComponentProps> = ({
  open,
  onClose,
  onSelectIcon,
  currentIcon,
}) => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Create styled icon component for dynamic icons
  const createStyledIconDisplay = useCallback(
    (IconComponent: React.ComponentType, isSelected: boolean) =>
      styled(IconComponent)(() => ({
        fontSize: 32,
        marginBottom: 8, // theme.spacing(0.5) equivalent
        color: isSelected ? 'primary.contrastText' : 'text.primary',
      })),
    []
  );

  const icons = useMemo(() => {
    setIsSearching(true);
    const results = searchIndex.search(query, 200);
    setTimeout(() => setIsSearching(false), 100); // Small delay to show loading
    return results;
  }, [query]);

  const handleIconSelect = useCallback(
    (iconName: string) => {
      onSelectIcon(iconName);
      onClose();
    },
    [onSelectIcon, onClose]
  );

  const handleClose = useCallback(() => {
    setQuery('');
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Select an Icon
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search from {Object.keys(MuiIcons).length}+ Material-UI icons
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="Search icons... (e.g., 'home', 'user', 'settings')"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
            variant="outlined"
            autoFocus
          />
        </Box>

        <Box mb={1}>
          <Typography variant="body2" color="text.secondary">
            {icons.length} icons found
          </Typography>
        </Box>

        <Box sx={{ height: 'calc(80vh - 200px)', overflow: 'auto' }}>
          <Grid container spacing={1}>
            {icons.map((iconName) => {
              const IconComponent = (MuiIcons as any)[iconName];
              if (!IconComponent) {
                return null;
              }

              const isSelected = currentIcon === iconName;

              return (
                <Grid item key={iconName}>
                  <Tooltip title={iconName} placement="top">
                    <StyledIconContainer
                      onClick={() => handleIconSelect(iconName)}
                      sx={{
                        backgroundColor: isSelected ? 'primary.light' : 'transparent',
                        color: isSelected ? 'primary.contrastText' : 'inherit',
                      }}
                    >
                      {(() => {
                        const DynamicStyledIcon = createStyledIconDisplay(
                          IconComponent,
                          isSelected
                        );
                        return <DynamicStyledIcon />;
                      })()}
                      <IconName
                        sx={{
                          color: isSelected ? 'primary.contrastText' : 'text.secondary',
                        }}
                      >
                        {iconName}
                      </IconName>
                    </StyledIconContainer>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {currentIcon && (
          <Button variant="contained" onClick={() => handleIconSelect(currentIcon)}>
            Keep Current ({currentIcon})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IconSearchComponent;
